import {
  EQUIPMENT_EMISSION_CUSTOM_REPORT_GROUP_BY,
  EQUIPMENT_EMISSION_CUSTOM_REPORT_STATIC_ROWS,
  EQUIPMENT_RECORD_CUSTOM_REPORT_GROUP_BY,
  EQUIPMENT_RECORD_CUSTOM_REPORT_STATIC_ROWS,
  FACILITY_RECORD_CUSTOM_REPORT_GROUP_BY,
  FLOW_RECORD_CUSTOM_REPORT_GROUP_BY,
  FLOW_RECORD_CUSTOM_REPORT_STATIC_ROWS,
} from "#batteries-included-components/Layouts/CustomReport/CustomReportConfigurationPanel/CustomReportConfigurationPanel.helpers";
import {
  CustomReportVariantType,
  CustomReportVariants,
} from "#batteries-included-components/Layouts/CustomReport/CustomReportDetailLayout.helpers";
import { RoutingLink } from "#batteries-included-components/RoutingLink";
import { useCustomAttributeFilters } from "#hooks/FilterPanel/useCustomAttributeFilters";
import { useListCalculators } from "#hooks/adapters/useCalculators";
import { FLOW_ATTRIBUTES, useFlowHeaders } from "#hooks/tables/useFlowHeaders";
import {
  EquipmentDetailRoute,
  linkToEquipmentDetail,
} from "#routes/organization/equipment/[equipmentId]";
import { linkToFacilityDetail } from "#routes/organization/facilities/[facilityId]";
import { useFacilityHeaders } from "#src/components/hooks/tables/useFacilityHeaders";
import { useMeasurementTypes } from "#src/contexts/MeasurementTypeContext";
import useLocalization from "#src/hooks/useLocalization";
import { linkToEstimationMethodDetailPage } from "#utils/links";
import { DataTable, HeaderType, Pill } from "@validereinc/common-components";
import {
  AssetType,
  CalculatorResultType,
  RecordType,
  SortDirection,
  type AssetTypeType,
} from "@validereinc/domain";
import {
  DateFormats,
  monthFormatter,
  toTitleCaseString,
} from "@validereinc/utilities";
import parse from "date-fns/parse";
import React, { useMemo } from "react";

export const useEquipmentEmissionsHeaders = (): {
  isLoading: boolean;
  headers: Array<HeaderType<CalculatorResultType>>;
} => {
  const { localize, isLoading: isLoadingLocalization } = useLocalization();
  const { getTypeName, isLoading: isLoadingMeasurementTypes } =
    useMeasurementTypes();

  const calculatorQuery = useListCalculators();
  const calculatorLookup = useMemo(
    () =>
      Object.fromEntries(
        calculatorQuery.data?.calculators?.map(
          ({ id, default_version, versions }) => [
            id,
            versions.find(({ version }) => version === default_version)?.title,
          ]
        ) ?? []
      ),
    [calculatorQuery.data]
  );

  const {
    customAttributeHeaders: facilityCustomAttributeHeaders,
    isLoading: isLoadingFacilityCustomAttrHeaders,
  } = useCustomAttributeFilters({
    assetType: AssetType.FACILITY,
    prefix: "equipment.facility.custom_attributes",
    sortable: true,
  });

  const {
    customAttributeHeaders: equipmentCustomAttributeHeaders,
    isLoading: isLoadingEquipmentCustomAttrHeaders,
  } = useCustomAttributeFilters({
    assetType: AssetType.EQUIPMENT,
    prefix: "equipment.custom_attributes",
    sortable: true,
  });

  return {
    isLoading:
      isLoadingLocalization ||
      isLoadingMeasurementTypes ||
      calculatorQuery.isLoading ||
      isLoadingFacilityCustomAttrHeaders ||
      isLoadingEquipmentCustomAttrHeaders,
    headers: [
      {
        key: EQUIPMENT_EMISSION_CUSTOM_REPORT_STATIC_ROWS.EQUIPMENT,
        label: localize("Equipment"),
        isSortable: true,
        renderComponent: ({ item }) => (
          <RoutingLink to={linkToEquipmentDetail(item["equipment.id"])}>
            {item[EQUIPMENT_EMISSION_CUSTOM_REPORT_STATIC_ROWS.EQUIPMENT]}
          </RoutingLink>
        ),
      },
      {
        key: EQUIPMENT_EMISSION_CUSTOM_REPORT_STATIC_ROWS.EQUIPMENT_STATUS,
        label: `${localize("Equipment")}: Status`,
        isSortable: true,
        renderComponent: ({ item }) => (
          <Pill
            variant={
              item["equipment.status"] === "active" ? "success" : "default"
            }
          >
            {toTitleCaseString(
              item[
                EQUIPMENT_EMISSION_CUSTOM_REPORT_STATIC_ROWS.EQUIPMENT_STATUS
              ],
              {
                intent: "ui",
                exceptions: ["oil-and-gas"],
              }
            )}
          </Pill>
        ),
      },
      {
        key: EQUIPMENT_EMISSION_CUSTOM_REPORT_STATIC_ROWS.EQUIPMENT_TYPE,
        label: `${localize("Equipment")}: Type`,
        isSortable: true,
        renderComponent: ({ item }) =>
          toTitleCaseString(item["equipment.type.name"], { intent: "ui" }),
      },
      {
        key: EQUIPMENT_EMISSION_CUSTOM_REPORT_STATIC_ROWS.FACILITY,
        label: localize("Facility"),
        isSortable: true,
        renderComponent: ({ item }) => (
          <RoutingLink to={linkToFacilityDetail(item["equipment.facility.id"])}>
            {item["equipment.facility.name"]}
          </RoutingLink>
        ),
      },
      {
        key: EQUIPMENT_EMISSION_CUSTOM_REPORT_STATIC_ROWS.FACILITY_STATUS,
        label: `${localize("Facility")}: Status`,
        isSortable: true,
        renderComponent: ({ item }) => (
          <Pill
            variant={
              item["equipment.facility.status"] === "active"
                ? "success"
                : "default"
            }
          >
            {toTitleCaseString(item["equipment.facility.status"], {
              intent: "ui",
              exceptions: ["oil-and-gas"],
            })}
          </Pill>
        ),
      },
      {
        key: EQUIPMENT_EMISSION_CUSTOM_REPORT_STATIC_ROWS.ESTIMATION_METHOD,
        label: "Estimation Method",
        isSortable: true,
        renderComponent: ({ item }) => (
          <RoutingLink
            to={linkToEstimationMethodDetailPage(
              item.entity_type ?? AssetType.FACILITY,
              item.entity_id ?? "",
              item.estimation_method_id ?? ""
            )}
          >
            {item.estimation_method_name}
          </RoutingLink>
        ),
      },
      {
        key: EQUIPMENT_EMISSION_CUSTOM_REPORT_STATIC_ROWS.MEASUREMENT_TYPE,
        label: "Measurement Type",
        isSortable: true,
        renderComponent: ({ item }: { item: CalculatorResultType }) =>
          item?.measurement_type && !!getTypeName
            ? getTypeName?.(item.measurement_type)
            : item?.measurement_type,
      },
      {
        key: EQUIPMENT_EMISSION_CUSTOM_REPORT_STATIC_ROWS.YEAR_MONTH,
        label: "Period",
        isSortable: true,
        renderComponent: ({
          item: { year_month: yearMonth },
        }: {
          item: CalculatorResultType;
        }) =>
          yearMonth
            ? monthFormatter(
                parse(yearMonth, DateFormats.YEAR_MONTH, new Date())
              )
            : "-",
      },
      {
        key: EQUIPMENT_EMISSION_CUSTOM_REPORT_STATIC_ROWS.CALCULATOR,
        label: "Calculator",
        isSortable: true,
        renderComponent: ({ item }: { item: CalculatorResultType }) =>
          (item["estimation_method.analytics_calculator_id"] &&
            calculatorLookup?.[
              item["estimation_method.analytics_calculator_id"]
            ]) ??
          item["estimation_method.analytics_calculator_id"],
      },
      ...facilityCustomAttributeHeaders,
      ...equipmentCustomAttributeHeaders,
    ],
  };
};

export const useEquipmentRecordsHeaders = ({
  enabled = true,
}: {
  enabled: boolean;
}): {
  isLoading: boolean;
  headers: Array<HeaderType<RecordType>>;
} => {
  const { localize } = useLocalization();

  const {
    customAttributeHeaders: equipmentCustomAttributeHeaders,
    isLoading: isLoadingEquipmentCustomAttrHeaders,
  } = useCustomAttributeFilters({
    assetType: AssetType.EQUIPMENT,
    prefix: "equipment.custom_attributes",
    sortable: true,
    enabled,
  });

  const {
    customAttributeHeaders: facilityCustomAttributeHeaders,
    isLoading: isLoadingFacilityCustomAttrHeaders,
  } = useCustomAttributeFilters({
    assetType: AssetType.FACILITY,
    prefix: "equipment.facility.custom_attributes",
    sortable: true,
  });

  return {
    isLoading:
      isLoadingEquipmentCustomAttrHeaders || isLoadingFacilityCustomAttrHeaders,
    headers: [
      {
        key: EQUIPMENT_RECORD_CUSTOM_REPORT_STATIC_ROWS.YEAR_MONTH,
        label: "Period",
        isSortable: true,
        renderComponent: ({ item: { year_month: yearMonth } }) =>
          yearMonth
            ? monthFormatter(
                parse(yearMonth, DateFormats.YEAR_MONTH, new Date())
              )
            : "-",
      },
      {
        key: EQUIPMENT_RECORD_CUSTOM_REPORT_STATIC_ROWS.EQUIPMENT,
        label: localize("Equipment"),
        isSortable: true,
        renderComponent: ({ item }) => (
          <RoutingLink
            to={EquipmentDetailRoute.toLinkParts({
              pathParams: { equipmentId: item["equipment.id"] },
            })}
          >
            {item[EQUIPMENT_RECORD_CUSTOM_REPORT_STATIC_ROWS.EQUIPMENT]}
          </RoutingLink>
        ),
      },
      {
        key: EQUIPMENT_RECORD_CUSTOM_REPORT_STATIC_ROWS.EQUIPMENT_STATUS,
        label: `${localize("Equipment")}: Status`,
        isSortable: true,
        renderComponent: ({ item }) => (
          <Pill
            variant={
              item["equipment.status"] === "active" ? "success" : "default"
            }
          >
            {toTitleCaseString(
              item[EQUIPMENT_RECORD_CUSTOM_REPORT_STATIC_ROWS.EQUIPMENT_STATUS],
              {
                intent: "ui",
                exceptions: ["oil-and-gas"],
              }
            )}
          </Pill>
        ),
      },
      {
        key: EQUIPMENT_RECORD_CUSTOM_REPORT_STATIC_ROWS.EQUIPMENT_TYPE,
        label: `${localize("Equipment")}: Type`,
        isSortable: true,
        renderComponent: ({ item }) =>
          toTitleCaseString(item["equipment.type.name"], { intent: "ui" }),
      },
      {
        key: EQUIPMENT_RECORD_CUSTOM_REPORT_STATIC_ROWS.FACILITY,
        label: localize("Facility"),
        isSortable: true,
        renderComponent: ({ item }) => (
          <RoutingLink to={linkToFacilityDetail(item["equipment.facility.id"])}>
            {item["equipment.facility.name"]}
          </RoutingLink>
        ),
      },
      {
        key: EQUIPMENT_RECORD_CUSTOM_REPORT_STATIC_ROWS.FACILITY_STATUS,
        label: `${localize("Facility")}: Status`,
        isSortable: true,
        renderComponent: ({ item }) => (
          <Pill
            variant={
              item["equipment.facility.status"] === "active"
                ? "success"
                : "default"
            }
          >
            {toTitleCaseString(item["equipment.facility.status"], {
              intent: "ui",
              exceptions: ["oil-and-gas"],
            })}
          </Pill>
        ),
      },
      ...equipmentCustomAttributeHeaders,
      ...facilityCustomAttributeHeaders,
    ],
  };
};

export const useFacilityRecordsHeaders = ({
  enabled = true,
}: {
  enabled: boolean;
}): {
  isLoading: boolean;
  headers: Array<HeaderType<RecordType>>;
} => {
  const {
    customAttributeHeaders: facilityCustomAttributeHeaders,
    isLoading: isLoadingFacilityCustomAttrHeaders,
  } = useCustomAttributeFilters({
    assetType: AssetType.FACILITY,
    prefix: "facility.custom_attributes",
    sortable: true,
    enabled,
  });

  const { headers: facilityHeaders, isLoading: isLoadingFacilityHeaders } =
    useFacilityHeaders<RecordType>({
      prefix: "facility",
      sortSubstitutions: {
        [FLOW_ATTRIBUTES.FACILITY.key]:
          FLOW_RECORD_CUSTOM_REPORT_STATIC_ROWS.FLOW_ASSOCIATED_FACILITY,
        [FLOW_ATTRIBUTES.EQUIPMENT.key]:
          FLOW_RECORD_CUSTOM_REPORT_STATIC_ROWS.FLOW_ASSOCIATED_EQUIPMENT,
      },
    });

  return {
    isLoading: isLoadingFacilityCustomAttrHeaders || isLoadingFacilityHeaders,
    headers: [...facilityHeaders, ...facilityCustomAttributeHeaders],
  };
};

export const useFlowRecordsHeaders = ({
  enabled = true,
}: {
  enabled: boolean;
}): {
  isLoading: boolean;
  headers: Array<HeaderType<RecordType>>;
} => {
  const {
    customAttributeHeaders: flowCustomAttributeHeaders,
    isLoading: isLoadingFlowCustomAttrHeaders,
  } = useCustomAttributeFilters({
    assetType: AssetType.FLOW,
    prefix: "flow.custom_attributes",
    sortable: true,
    enabled,
  });

  const {
    customAttributeHeaders: facilityCustomAttributeHeaders,
    isLoading: isLoadingFacilityCustomAttrHeaders,
  } = useCustomAttributeFilters({
    assetType: AssetType.FACILITY,
    prefix: "flow.associated_facility.custom_attributes",
    sortable: true,
  });

  const {
    customAttributeHeaders: equipmentCustomAttributeHeaders,
    isLoading: isLoadingEquipmentCustomAttrHeaders,
  } = useCustomAttributeFilters({
    assetType: AssetType.EQUIPMENT,
    prefix: "flow.associated_equipment.custom_attributes",
    sortable: true,
  });

  const { isLoading: isLoadingFlowHeaders, headers: flowHeaders } =
    useFlowHeaders<RecordType>({
      prefix: "flow",
      sortSubstitutions: {
        [FLOW_ATTRIBUTES.FACILITY.key]:
          FLOW_RECORD_CUSTOM_REPORT_STATIC_ROWS.FLOW_ASSOCIATED_FACILITY,
        [FLOW_ATTRIBUTES.EQUIPMENT.key]:
          FLOW_RECORD_CUSTOM_REPORT_STATIC_ROWS.FLOW_ASSOCIATED_EQUIPMENT,
      },
    });

  return {
    isLoading:
      isLoadingFlowCustomAttrHeaders ||
      isLoadingFacilityCustomAttrHeaders ||
      isLoadingEquipmentCustomAttrHeaders ||
      isLoadingFlowHeaders,
    headers: [
      ...flowHeaders,
      {
        key: FLOW_RECORD_CUSTOM_REPORT_STATIC_ROWS.YEAR_MONTH,
        label: "Period",
        isSortable: true,
        renderComponent: ({
          item: { year_month: yearMonth },
        }: {
          item: RecordType;
        }) =>
          yearMonth
            ? monthFormatter(
                parse(yearMonth, DateFormats.YEAR_MONTH, new Date())
              )
            : null,
      },
      ...flowCustomAttributeHeaders,
      ...equipmentCustomAttributeHeaders,
      ...facilityCustomAttributeHeaders,
    ],
  };
};

export const useOutputHeaders = (
  variant: CustomReportVariantType,
  outputs: string[] = []
):
  | { isLoading: boolean; headers: Array<HeaderType<RecordType>> }
  | {
      isLoading: boolean;
      headers: Array<HeaderType<CalculatorResultType>>;
    } => {
  const {
    getTypeName,
    getUnitName,
    getUnitByType,
    getPrecisionByType,
    isLoading: isLoadingMeasurementTypes,
  } = useMeasurementTypes();

  switch (variant) {
    case CustomReportVariants.RECORDS:
      return {
        isLoading: isLoadingMeasurementTypes,
        headers: outputs.map((key) => {
          const unit = getUnitName(getUnitByType(key));

          return {
            key,
            label: `${getTypeName(key)}${unit ? ` [${unit}]` : ""}`,
            alignment: "right",
            isSortable: false,
            renderComponent: (componentProps: { item: RecordType }) => {
              const { item } = componentProps;
              const matchingValue = item?.values?.find(
                ({ measurement_type }) => measurement_type === key
              );
              const precision = getPrecisionByType(key);

              return (
                <DataTable.DataRow.NumberCell
                  value={matchingValue?.value ?? ""}
                  formattingOpts={
                    precision
                      ? {
                          fractionDigits: precision,
                        }
                      : {}
                  }
                />
              );
            },
          };
        }),
      };
    case CustomReportVariants.EMISSIONS:
      return {
        isLoading: isLoadingMeasurementTypes,
        headers: outputs.map((key) => {
          const unit = getUnitName(getUnitByType(key));

          return {
            key: `measurement.${key}`,
            alignment: "right",
            // the label maps 1:1 to the output config options. These headers are
            // filtered against the output options selected using this label.
            label: `${getTypeName(key)}${unit ? ` [${unit}]` : ""}`,
            isSortable: true,
            renderComponent: (componentProps: {
              item: CalculatorResultType;
            }) => {
              const { item } = componentProps;
              const precision = getPrecisionByType(key);

              return (
                <DataTable.DataRow.NumberCell
                  value={item?.measurement?.[key]}
                  formattingOpts={
                    precision
                      ? {
                          fractionDigits: precision,
                        }
                      : {}
                  }
                />
              );
            },
          };
        }),
      };
    default:
      return {
        isLoading: false,
        headers: [],
      };
  }
};

export const getCustomReportDefaultSort = (
  variant: CustomReportVariantType,
  assetType: AssetTypeType,
  rows: string[] = []
) => {
  let groupByMap: Record<string, string[]> = {};
  switch (variant) {
    case CustomReportVariants.EMISSIONS:
      groupByMap = EQUIPMENT_EMISSION_CUSTOM_REPORT_GROUP_BY;
      break;
    case CustomReportVariants.RECORDS:
      groupByMap =
        assetType === AssetType.FACILITY
          ? FACILITY_RECORD_CUSTOM_REPORT_GROUP_BY
          : assetType === AssetType.EQUIPMENT
            ? EQUIPMENT_RECORD_CUSTOM_REPORT_GROUP_BY
            : assetType === AssetType.FLOW
              ? FLOW_RECORD_CUSTOM_REPORT_GROUP_BY
              : {};
      break;
    default:
  }
  const sortDirection = SortDirection.ASCENDING;
  const keys = groupByMap[rows[0]];
  return keys?.length
    ? { sortBy: keys[keys.length - 1], sortDirection }
    : rows.length
      ? { sortBy: rows[0], sortDirection }
      : {};
};
